import {chain} from 'lodash';
import memoize from 'lru-memoize';
import React, {FC, ReactNode, useMemo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {intersperseNodes} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {TeammateModel} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {filterRenderers} from '../../../../analytics/helpers/analyticsFiltersHelpers';
import {doesTeammateMatchQuery} from '../../../../helpers/modelHelpers';
import {AnalyticsFilterTypesEnum} from '../../../../models/api/analyticsSavedViewModel';
import {FilterRowGenerator} from '../../filters/resourceMultiSelectFilterDropdown';
import {computeFilterDropdownSourceAsync} from '../../filters/sourceFilter/filterDropdownSource';
import {ResourceMultiSelectInput} from '../../resourceMultiSelectInput/resourceMultiSelectInput';
import {DropdownItemTeammateAvatar} from '../dropdownItemTeammateAvatar';
import {DropdownFetcher} from '../source/asyncDropdownSource';

/*
 * Props.
 */

interface PickTeammateWorkspacesDropdownBoxProps {
  teammates: ReadonlyArray<TeammateModel>;
  selectedTeammateIds: ReadonlyArray<number>;
  onSelectedTeammatesChange: (arg: ReadonlyArray<number>) => void;
  label?: ReactNode;
  title?: ReactNode;
  isRequired?: boolean;
  shouldHideLabel?: boolean;
  width?: string;
}

/*
 * Style.
 */

const StyledValueWrapper = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  ${ellipsis()}
`;

/*
 * Component.
 */

export const PickTeammateWorkspacesDropdownBox: FC<PickTeammateWorkspacesDropdownBoxProps> = (props) => {
  const dropdownSource = useMemo(() => {
    const fetcher: DropdownFetcher<TeammateModel> = async (query) => ({
      items: filterRowsByQuery(props.teammates, query),
      canLoadMore: false,
      paginationData: {},
    });
    return computeFilterDropdownSourceAsync(fetcher);
  }, [props.teammates]);

  return (
    <ResourceMultiSelectInput
      dropdownSource={dropdownSource}
      selectedItemIds={props.selectedTeammateIds}
      computeFilterGenerator={buildRowGenerator}
      dropdownTitle={
        props.title ?? <Trans id="tags-form-multiselect-assignees-dropdown-title">Teammates</Trans>
      }
      isDisabled={false}
      isRequired={props.isRequired ?? false}
      label={!props.shouldHideLabel ? props.label : null}
      onSetItems={props.onSelectedTeammatesChange}
      renderPlaceholder={() => (
        <Trans id="tags-form-multiselect-assignees-placeholder">Select teammates</Trans>
      )}
      renderValue={() => renderValue(props)}
      onClearAll={() => props.onSelectedTeammatesChange([])}
      rightIcon={
        <Button
          size={VisualSizesEnum.SMALL}
          variant={ButtonVariantsEnum.LINK}
          onClick={() => {
            props.onSelectedTeammatesChange(props.teammates.map((teammate) => teammate.id));
          }}
        >
          <Trans id="settings-pick-teammate-workspace-selectall">Select all</Trans>
        </Button>
      }
    />
  );
};

/*
 * Helpers.
 */

function renderValue(props: PickTeammateWorkspacesDropdownBoxProps) {
  const {selectedTeammateIds, teammates} = props;

  if (!selectedTeammateIds.length) {
    return null;
  }

  const selectedTeammates = chain(selectedTeammateIds)
    .map((id) => teammates.find((teammate) => teammate.id === id))
    .compact()
    .value();

  return (
    <StyledValueWrapper style={{width: props.width}}>
      {intersperseNodes(
        selectedTeammates.map((teammate) => ({
          node: filterRenderers[AnalyticsFilterTypesEnum.TEAMMATES].renderItem(teammate),
          key: String(teammate.id),
        })),
        () => ', ',
      )}
    </StyledValueWrapper>
  );
}

const buildRowGenerator: FilterRowGenerator<TeammateModel> = (
  teammate,
  dropdownSelectedIds,
  makeOnItemSelect,
) => {
  const isSelected = dropdownSelectedIds.includes(teammate.id);

  return {
    isSelected,
    renderer: () => (
      <>
        <DropdownItemTeammateAvatar teammate={teammate} />
        <DropdownItemContent isChecked={isSelected}>{buildTeammateDisplayName(teammate)}</DropdownItemContent>
      </>
    ),
    onSelect: makeOnItemSelect(teammate.id),
    id: teammate.id,
  };
};

const filterRowsByQuery = memoize(20)((teammates: ReadonlyArray<TeammateModel>, searchQuery: string) => {
  // For empty query, render all choices.
  if (!searchQuery) {
    return teammates;
  }

  // Otherwise, filter by name.
  return chain(teammates)
    .filter((teammate) => doesTeammateMatchQuery(teammate, searchQuery))
    .value();
});
